import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import Panel from '../../components/cards/Panel'
import PageHeader from '../../components/common/PageHeader'
import ErrorState from '../../components/common/ErrorState'
import EmptyState from '../../components/common/EmptyState'
import { Skeleton } from '../../components/common/Loading'
const types = [
  'daily-appointments',
  'appointments',
  'appointment-status',
  'patients',
  'departments',
  'services',
  'consultation-mode',
  'payments',
  'revenue',
  'refunds',
  'prescriptions',
]
export default function ReportsPage({ role }: { role: 'doctor' | 'admin' }) {
  const [type, setType] = useState('appointments'),
    [from, setFrom] = useState(''),
    [to, setTo] = useState(''),
    [status, setStatus] = useState(''),
    [service, setService] = useState(''),
    [department, setDepartment] = useState(''),
    [mode, setMode] = useState(''),
    [page, setPage] = useState(1)
  const params = {
    from: from || undefined,
    to: to || undefined,
    status: status || undefined,
    service_id: service || undefined,
    department_id: department || undefined,
    consultation_mode: mode || undefined,
    page,
  }
  const path = '/' + role + '/reports/' + type
  const q = useQuery({
    queryKey: ['reports', role, type, params],
    queryFn: async () =>
      (
        await apiClient.get<
          ApiResponse<Record<string, unknown>[]> & {
            totals: { records: number; gross_amount: string | null }
          }
        >(path, { params })
      ).data,
  })
  return (
    <div className="page-container">
      <PageHeader
        title="Reports"
        description="Filters use appointment dates. Exports contain only authorized records."
      />
      <Panel title="Report filters">
        <div className="form-grid">
          <label>
            Report
            <select
              className="form-select"
              value={type}
              onChange={(e) => {
                setType(e.target.value)
                setPage(1)
              }}
            >
              {types.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          {[
            ['From', from, setFrom, 'date'],
            ['To', to, setTo, 'date'],
            ['Status', status, setStatus, 'text'],
            ['Service ID', service, setService, 'number'],
            ['Department ID', department, setDepartment, 'number'],
            ['Mode', mode, setMode, 'text'],
          ].map(([label, value, setter, inputType]) => (
            <label key={String(label)}>
              {String(label)}
              <input
                className="form-control"
                type={String(inputType)}
                min={inputType === 'number' ? 1 : undefined}
                value={String(value)}
                onChange={(e) =>
                  (setter as (v: string) => void)(e.target.value)
                }
              />
            </label>
          ))}
        </div>
        <div className="form-actions">
          <a
            className="btn btn-outline-primary"
            href={apiClient.getUri({
              url: path,
              params: { ...params, format: 'csv' },
            })}
          >
            Export CSV
          </a>
          <a
            className="btn btn-outline-primary"
            target="_blank"
            rel="noreferrer"
            href={apiClient.getUri({
              url: path,
              params: { ...params, format: 'pdf' },
            })}
          >
            Print PDF
          </a>
        </div>
      </Panel>
      <Panel className="mt-3" title="Results">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : !q.data?.data.length ? (
          <EmptyState title="No matching records" />
        ) : (
          <>
            <p>
              {q.data.totals.records} records{' '}
              {q.data.totals.gross_amount &&
                '· Gross amount ' + q.data.totals.gross_amount}
            </p>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    {Object.keys(q.data.data[0]).map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data.data.map((r, i) => (
                    <tr key={i}>
                      {Object.values(r).map((v, j) => (
                        <td key={j}>{String(v ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="form-actions">
          <button
            disabled={page === 1}
            className="btn btn-outline-secondary"
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <button
            disabled={!q.data?.meta || page >= q.data.meta.last_page}
            className="btn btn-outline-secondary"
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </Panel>
    </div>
  )
}
